import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {QRCodeSVG} from 'qrcode.react';

import Logo from "../public/eventli-logo.svg"

const BookingConfirmationPage = () => {
  const { bookingId } = useParams();
  const location = useLocation();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Try to get booking details from location state first
  useEffect(() => {
    if (location.state?.bookingDetails) {
      setBooking(location.state.bookingDetails);
      setLoading(false);
    } else if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId, location.state]);
  
  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://event-li-backend.onrender.com/booking/${bookingId}`);
      setBooking(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load booking details. Please check your booking ID.');
      setLoading(false);
    }
  };
  
  const handleDownloadTicket = async () => {
    try {
      const response = await axios.get(`https://event-li-backend.onrender.com/booking/${bookingId}/download-ticket`, {
        responseType: 'blob'
      });
      
      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket-${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download ticket:', err);
      alert('Failed to download ticket. Please try again later.');
    }
  };
  
  // Format date for display
  const formatEventDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  // Format time for display
  const formatEventTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.3 }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="text-red-500 mb-4">{error}</div>
            <Link to="/" className="text-blue-600 hover:underline">Return to home page</Link>
          </div>
        ) : booking ? (
          <motion.div 
            className="bg-white rounded-lg shadow-sm overflow-hidden"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Back Button and Logo */}
            <div className="bg-white p-4 flex items-center justify-between border-b border-gray-100">
              <Link to="/" className="flex items-center text-gray-600 hover:text-gray-900">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span>Back to main page</span>
              </Link>
              <img 
                src={Logo} 
                alt="Eventli Logo" 
                className="h-10 w-auto"
              />
            </div>

            {/* Header */}
            <div className="bg-green-50 p-6 flex items-center gap-4 border-b border-green-100">
              <motion.div 
                className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"
                variants={itemVariants}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
              <motion.div variants={itemVariants}>
                <h2 className="text-xl font-semibold text-gray-800">Your Booking is Confirmed!</h2>
              </motion.div>
            </div>
            
            <div className="p-6">
              {/* Event Details */}
              <div className="border-b border-gray-200 pb-1 mb-4">
                <h3 className="text-sm uppercase tracking-wider text-gray-500 font-medium">Event Information</h3>
              </div>
              <motion.div className="mb-8" variants={itemVariants}>
                <h3 className="text-gray-600 mb-2">You are attending</h3>
                <p className="text-xl font-semibold text-gray-800">{booking.eventTitle || "Nigeria Female Entrepreneurs Conference"}</p>
                
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-gray-800">
                        {formatEventDate(booking.eventStartDate)} - {formatEventTime(booking.eventStartDate)} ({booking.timezone || "GMT+01:00"})
                      </p>
                      <button className="text-sm text-blue-600 hover:underline mt-1">Add to Calendar</button>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-gray-800">{booking.eventLocation || "The Zone, Plot 9 Gbagada Industrial Scheme, Beside UPS, Gbagada ExpressWay, Lagos Nigeria"}</p>
                  </div>
                </div>
              </motion.div>
              
              {/* Booking Details */}
              <div className="border-b border-gray-200 pb-1 mb-4">
                <h3 className="text-sm uppercase tracking-wider text-gray-500 font-medium">Ticket Information</h3>
              </div>
              <motion.div variants={itemVariants} className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Your Booking Details</h3>
                
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* QR Code */}
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <QRCodeSVG 
                      value={`BOOKING:${bookingId}`} 
                      size={128}
                      level="H"
                      renderAs="svg"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <p className="text-lg font-medium text-gray-800">{booking.customerName || "Oluchi Davies"}</p>
                    <p className="text-gray-600 mt-1">Booking ID: {booking.bookingId || bookingId}</p>
                    
                    <div className="mt-4 text-right">
                      <div className="text-gray-600">
                        {booking.isFree ? "Free Entry" : `Paid - ₦${booking.totalAmount?.toLocaleString() || "0.00"}`}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Payment Summary */}
              <div className="border-b border-gray-200 pb-1 mb-4">
                <h3 className="text-sm uppercase tracking-wider text-gray-500 font-medium">Payment Information</h3>
              </div>
              <motion.div variants={itemVariants} className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Payment Summary</h3>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-green-600">Completed</span>
                  </div>
                  
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Date:</span>
                    <span className="text-gray-800">{booking.paymentDate || new Date().toLocaleDateString()}</span>
                  </div>
                  
                  <div className="border-t border-gray-200 my-2"></div>
                  
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Ticket Price:</span>
                    <span className="text-gray-800">{booking.isFree ? "0.00" : `₦${booking.ticketPrice?.toLocaleString() || "0.00"}`}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Paid amount:</span>
                    <span className="text-gray-800 font-medium">{booking.isFree ? "0.00" : `₦${booking.totalAmount?.toLocaleString() || "0.00"}`}</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Email Notification */}
              <div className="border-b border-gray-200 pb-1 mb-4">
                <h3 className="text-sm uppercase tracking-wider text-gray-500 font-medium">Additional Information</h3>
              </div>
              <motion.div variants={itemVariants} className="text-gray-600 mb-8">
                <p>We have shared a copy of this ticket with you at {booking.customerEmail || "luchi@gmail.com"}</p>
              </motion.div>
              
              {/* Download Button */}
              <motion.div variants={itemVariants} className="text-center">
                <button 
                  onClick={handleDownloadTicket}
                  className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 transition-colors rounded-full font-semibold text-gray-900"
                >
                  Download Tickets
                </button>
              </motion.div>
            </div>
          </motion.div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="text-gray-600 mb-4">No booking information found.</div>
            <Link to="/" className="text-blue-600 hover:underline">Return to home page</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingConfirmationPage;